import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { LOGO_URL as DEFAULT_LOGO } from "./constants";

const BrandingCtx = createContext({
    logoUrl: DEFAULT_LOGO,
    faviconUrl: "",
    logoHeight: 40,
    radio: { stream_url: "", on_air: false, now_playing: "" },
    refresh: () => {},
});

let gaInjected = false;

function setFavicon(href) {
    if (!href) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    link.href = href;
}

function applyFontStyle(settings) {
    let el = document.getElementById("brand-style");
    if (!el) {
        el = document.createElement("style");
        el.id = "brand-style";
        document.head.appendChild(el);
    }
    const rules = [];
    if (settings.font_hero_px) {
        rules.push(`[data-testid="hero-title"]{font-size:${settings.font_hero_px}px !important;line-height:0.95;}`);
    }
    if (settings.font_section_px) {
        rules.push(`.brand-section{font-size:${settings.font_section_px}px !important;line-height:1;}`);
    }
    if (settings.font_body_px) {
        rules.push(`body{font-size:${settings.font_body_px}px;}`);
    }
    if (settings.font_nav_px) {
        rules.push(`.brand-nav{font-size:${settings.font_nav_px}px !important;letter-spacing:0.15em;}`);
    }
    if (settings.logo_height_px) {
        rules.push(`.brand-logo{height:${settings.logo_height_px}px !important;width:auto;}`);
    }
    el.textContent = rules.join("\n");
}

function injectGA(id) {
    if (!id || gaInjected) return;
    gaInjected = true;
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`;
    document.head.appendChild(s2);
}

export function BrandingProvider({ children }) {
    const [state, setState] = useState({
        logoUrl: DEFAULT_LOGO,
        faviconUrl: "",
        logoHeight: 40,
        radio: { stream_url: "", on_air: false, now_playing: "" },
    });

    const load = useCallback(async () => {
        try {
            const r = await api.get("/settings/public");
            const d = r.data || {};
            const next = {
                logoUrl: d.logo_url || DEFAULT_LOGO,
                faviconUrl: d.favicon_url || "",
                logoHeight: d.logo_height_px || 40,
                radio: {
                    stream_url: d.radio_stream_url || "",
                    on_air: !!d.radio_on_air,
                    now_playing: d.radio_now_playing || "",
                },
            };
            setState(next);
            applyFontStyle(d);
            setFavicon(d.favicon_url);
            injectGA(d.ga_tracking_id);
        } catch (err) {
            // Branding endpoint is optional — site still works with defaults. Log so
            // a misconfigured backend isn't silently invisible during debugging.
            // eslint-disable-next-line no-console
            console.warn("Branding load failed, using defaults:", err);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <BrandingCtx.Provider value={{ ...state, refresh: load }}>
            {children}
        </BrandingCtx.Provider>
    );
}

export const useBranding = () => useContext(BrandingCtx);
